import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import {
  useDeviceUsers,
  useRemoveUserFromDevice,
} from "hooks/useDevices";
import { useQueryClient } from "@tanstack/react-query";
import { DeviceUser } from "types/device.types";
import { format } from "date-fns";
import AddUserToDeviceDialog from "./AddUserToDeviceDialog";
import styled from "styled-components";
import { theme } from "styles/theme";
import { useAuth } from "context/AuthContext";
import { notify } from "utils/notify";
import { LoadingScreen } from "components/shared/LoadingScreen";
import { PageError } from "components/shared/PageError";
import { EmptyState } from "components/shared/EmptyState";
import { AppButton } from "components/shared/AppButton";
import { RoleBadge } from "styles/components/usersList.styles";

interface DeviceUsersProps {
  serialNumber: string;
  deviceId: number;
}

const Container = styled.div`
  margin-top: 1.5rem;
  color: ${theme.colors.textPrimary};

  .p-datatable {
    background: transparent;
    border: none;
    border-radius: 0;
    overflow: visible;
  }

  .p-datatable .p-datatable-thead > tr > th {
    background: transparent;
    color: ${theme.colors.textPrimary};
    border: none;
    border-bottom: 2px solid var(--davey-section-header-border);
  }

  .p-datatable .p-datatable-tbody > tr {
    cursor: pointer;
    background: transparent;
  }

  .p-datatable .p-datatable-tbody > tr > td {
    border-color: ${theme.colors.border};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.h3`
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const ActionButton = styled(Button)`
  width: 2.5rem;
  height: 2.5rem;
`;

const DeviceUsers: React.FC<DeviceUsersProps> = ({
  serialNumber,
  deviceId,
}) => {
  const navigate = useNavigate();
  const [addUserDialogVisible, setAddUserDialogVisible] = useState(false);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DeviceUser | null>(null);

  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const { data, isLoading, error, refetch } = useDeviceUsers(deviceId.toString());
  const removeUserMutation = useRemoveUserFromDevice();

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  const confirmRemoveUser = (deviceUser: DeviceUser) => {
    setSelectedUser(deviceUser);
    setConfirmDialogVisible(true);
  };

  const handleRemoveUser = () => {
    if (!selectedUser) return;

    removeUserMutation.mutate(
      { deviceId, userId: selectedUser.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["deviceUsers"] });
          notify.success(
            `User ${selectedUser.email} has been removed from this device`,
          );
          queryClient.invalidateQueries({
            queryKey: ["deviceUsers", serialNumber],
          });
          setSelectedUser(null);
        },
        onError: (err: any) => {
          notify.error(
            err.response?.data?.message || "Failed to remove user",
          );
        },
      },
    );
  };

  const actionTemplate = (rowData: DeviceUser) => (
    <ActionButton
      type="button"
      icon="pi pi-trash"
      className="p-button-danger p-button-rounded"
      onClick={(e) => {
        e.stopPropagation();
        confirmRemoveUser(rowData);
      }}
      aria-label={`Remove ${rowData.email}`}
    />
  );

  const roleTemplate = (rowData: DeviceUser) => (
    <RoleBadge role={rowData.privledgelevel || "USER"}>
      {rowData.privledgelevel?.toLowerCase() || "user"}
    </RoleBadge>
  );

  const dateTemplate = (rowData: DeviceUser, field: string) =>
    formatDate(rowData[field as keyof DeviceUser] as string);

  if (!isAdmin) return null;

  if (isLoading) {
    return (
      <Container>
        <Title>Device Users</Title>
        <LoadingScreen message="Loading device users..." />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container>
        <Title>Device Users</Title>
        <PageError
          title="Error loading device users"
          message="Please try again later."
          onRetry={() => refetch()}
        />
      </Container>
    );
  }

  const users = data || [];

  return (
    <Container>
      <Header>
        <Title>Device Users</Title>
        <AppButton
          type="button"
          onClick={() => setAddUserDialogVisible(true)}
        >
          Add User
        </AppButton>
      </Header>

      {users.length === 0 ? (
        <EmptyState
          message="No users are currently associated with this device."
          actionLabel="Add User"
          onAction={() => setAddUserDialogVisible(true)}
        />
      ) : (
        <DataTable
          value={users}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          stripedRows
          tableStyle={{ minWidth: "50rem" }}
          onRowClick={(e) => navigate(`/users/${(e.data as DeviceUser).id}`)}
        >
          <Column field="firstname" header="First Name" sortable />
          <Column field="lastname" header="Last Name" sortable />
          <Column field="email" header="Email" sortable />
          <Column
            field="privledgelevel"
            header="Role"
            sortable
            body={roleTemplate}
          />
          <Column
            field="createdAt"
            header="Created At"
            sortable
            body={(rowData) => dateTemplate(rowData, "createdAt")}
          />
          <Column
            body={actionTemplate}
            headerStyle={{ width: "6rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
        </DataTable>
      )}

      <AddUserToDeviceDialog
        visible={addUserDialogVisible}
        onHide={() => setAddUserDialogVisible(false)}
        deviceId={deviceId}
        serialNumber={serialNumber}
      />

      <ConfirmDialog
        visible={confirmDialogVisible}
        onHide={() => setConfirmDialogVisible(false)}
        message={
          selectedUser
            ? `Are you sure you want to remove ${selectedUser.firstname} ${selectedUser.lastname} (${selectedUser.email}) from this device?`
            : "Are you sure you want to remove this user?"
        }
        header="Confirm Removal"
        icon="pi pi-exclamation-triangle"
        accept={handleRemoveUser}
        reject={() => setConfirmDialogVisible(false)}
        acceptClassName="p-button-danger"
      />
    </Container>
  );
};

export default DeviceUsers;
